import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import { Phone, Video } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useShellStore } from "../../../store";
import { videoService } from "../api/video.api";
import { useIncomingCalls } from "../hooks/useIncomingCalls";
import { useCallSound } from "../hooks/useCallSound";
import { useTopLayer } from "../hooks/useTopLayer";
import { useRingtoneElection } from "../hooks/useRingElection";
import { useTitleFlash } from "../hooks/useTitleFlash";
import { useCallSessionStore } from "../store/callSessionStore";
import { useIncomingRingStore } from "../store/incomingRingStore";
import { HangUpIcon } from "./HangUpIcon";
import { createJoinAudioContext } from "../lib/callRoomOptions";

/**
 * Global "someone is calling you" banner for 1:1 direct calls. Mounted once
 * at the Shell level (main.tsx), independent of <CallSessionRoom>/LiveKit —
 * the callee isn't connected to anything until they accept, so this can't
 * live inside the LiveKit-context tree the rest of the call UI does.
 */
export function IncomingCallBanner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAuthenticated = useShellStore((s) => s.isAuthenticated);
  const active = useCallSessionStore((s) => s.active);
  const setActive = useCallSessionStore((s) => s.setActive);

  const invite = useIncomingCalls(isAuthenticated && !active);
  const [dismissedId, setDismissedId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const showing = Boolean(invite) && invite?.id !== dismissedId;
  const callerName = invite ? invite.caller.full_name || invite.caller.short_name || "Someone" : "";

  // D7 + D8 — the banner always shows, but only one place makes a sound: not
  // while a ringback is playing anywhere, and only in one tab per invite. The
  // silent tabs still render, because a banner you cannot answer is worse than
  // a banner that does not ring.
  const shouldRing = useRingtoneElection(invite?.id ?? null, showing);

  // D4 — ringtone for the callee, tied to the banner being shown. Must be
  // called above the early return below: the banner unmounts itself with
  // `return null`, and a hook after that point would break the rules of hooks.
  // Accepting and declining both set `dismissedId`; the caller cancelling and
  // the 45 s ring timeout both stop the invite coming back from the poll. All
  // four therefore stop the ringtone through this one condition.
  const { blocked } = useCallSound("ringtone", shouldRing);

  // D5 — if the browser refused to play, say it in the tab title instead. Only
  // the tab that actually tried to ring flashes: a tab held silent by D7/D8 is
  // quiet by design, not blocked, and `blocked` stays false there.
  useTitleFlash(showing && blocked, t("call.ringing.titleFlash", { name: callerName }));

  // D1 — the banner is rendered into the browser's top layer rather than at a
  // z-index, so a slideshow, a dialog or an open menu cannot bury it. Must be
  // called above the early return below, like the hooks before it.
  const topLayer = useTopLayer<HTMLDivElement>();

  // Publish "a call is ringing for me" so other shell surfaces (e.g. a dot
  // on the sidebar Call item) can read it without polling a second time.
  const setRinging = useIncomingRingStore((s) => s.setRinging);
  useEffect(() => {
    setRinging(showing);
    return () => setRinging(false);
  }, [showing, setRinging]);

  if (!invite || !showing) return null;

  const handleAccept = async () => {
    setBusy(true);
    // Create the AudioContext synchronously inside this click's user-gesture
    // window (see RoomRoute.handleJoin) so iOS Chrome routes call audio to
    // the loudspeaker instead of the earpiece.
    const audioContext = createJoinAudioContext();
    try {
      const resolved = await videoService.respondToDirectCall(invite.id, true);
      if (!resolved.livekit) {
        toast.error(t("call.ringing.callCanceled"));
        setDismissedId(invite.id);
        return;
      }
      setActive({
        slug: resolved.room.slug ?? resolved.room.id,
        roomId: resolved.room.id,
        roomName: resolved.room.name,
        url: resolved.livekit.url,
        token: resolved.livekit.token,
        // Join muted with the camera off, matching the caller side; the
        // callee can unmute or start video from the in-call controls.
        choices: { username: "", audioEnabled: false, videoEnabled: false },
        joinedAt: Date.now(),
        audioContext,
      });
      setDismissedId(invite.id);
      navigate(`/call/${resolved.room.slug ?? resolved.room.id}`);
    } catch (err) {
      console.error("Failed to accept direct call:", err);
      toast.error(t("call.ringing.callCanceled"));
      setDismissedId(invite.id);
    } finally {
      setBusy(false);
    }
  };

  const handleDecline = async () => {
    setDismissedId(invite.id);
    try {
      await videoService.respondToDirectCall(invite.id, false);
    } catch (err) {
      console.error("Failed to decline direct call:", err);
    }
  };

  return (
    <div ref={topLayer.ref} popover={topLayer.popover} className="call-top-layer">
      <AnimatePresence>
        {/* The design's incoming-call pill (weco2/shell.jsx IncomingCallBanner). */}
        <motion.div
          key={invite.id}
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -16 }}
          transition={{ type: "spring", damping: 26, stiffness: 280 }}
          role="alertdialog"
          aria-label={t("call.ringing.incomingCallTitle")}
          className="fixed inset-x-0 z-[200] flex justify-center pointer-events-none px-2"
          style={{ top: "max(12px, env(safe-area-inset-top))" }}
        >
          <div className="pointer-events-auto flex items-center gap-3 sm:gap-4 w-full max-w-[460px] bg-background border border-border shadow-2xl rounded-full ps-3 pe-3 sm:pe-4 py-3">
            <div className="relative shrink-0">
              <span className="size-12 rounded-full bg-neutral-600 text-white flex items-center justify-center font-bold text-lg select-none">
                {callerName.trim().charAt(0).toUpperCase()}
              </span>
              <span className="absolute inset-0 rounded-full ring-2 ring-primary anim-pulse-rec" />
            </div>
            <div className="min-w-0 flex-1 pe-1">
              <p className="font-black text-sm leading-tight truncate text-foreground">{callerName}</p>
              <p className="text-[12px] text-muted-foreground font-bold flex items-center gap-2">
                <Video className="size-3 text-primary shrink-0" />
                <span className="truncate">
                  {t("call.ringing.incomingWeCoCall", { defaultValue: "Incoming We-Co Call…" })}
                </span>
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => void handleDecline()}
                disabled={busy}
                title={t("call.ringing.decline")}
                aria-label={t("call.ringing.decline")}
                className="size-12 rounded-full bg-red-500 text-white flex items-center justify-center hover:bg-red-600 active:scale-95 transition-all shadow-lg shadow-red-500/20 disabled:opacity-50"
              >
                <HangUpIcon className="size-5" />
              </button>
              <button
                type="button"
                onClick={() => void handleAccept()}
                disabled={busy}
                title={t("call.ringing.accept")}
                aria-label={t("call.ringing.accept")}
                className="size-12 rounded-full bg-emerald-500 text-white flex items-center justify-center hover:bg-emerald-600 active:scale-95 transition-all shadow-lg shadow-emerald-500/20 anim-pulse-rec disabled:opacity-50"
              >
                <Phone className="size-5" />
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
